import {Component, EventEmitter, Input, OnInit, Output, ViewChild, ViewEncapsulation} from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup } from '@angular/forms';
import { Dialog, FileUpload, TabView } from 'primeng/primeng';
import { IResource } from '../../../strategies/services/IResource';
import { ValidationService } from '../../../auth/services/validation.service';
import { ResourceService } from '../../../strategies/services/resource.service';
import { ValidationErrors } from '@angular/forms/src/directives/validators';
import { TokenService } from '../../../auth/services/token.service';
import {AppLoadingService} from '../../../app-loading-panel/app-loading.service';

@Component({
  selector: 'app-attachment-upload-modal',
  templateUrl: './attachment-upload-modal.component.html',
  styleUrls: ['./attachment-upload-modal.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AttachmentUploadModalComponent implements OnInit {

  readonly MAX_LINK_LENGTH = 100;
  readonly MAX_NAME_LENGTH = 100;
  readonly MAX_DESCRIPTION_LENGTH = 300;

  private readonly ATTACH_FILES_TAB_INDEX = 0;
  private readonly EXTERNAL_LINK_TAB_INDEX = 1;

  private readonly EXTENSION_RE = /\.[0-9a-z]+$/i;

  @ViewChild('fileUpload') private fileUpload: FileUpload;
  @ViewChild('attachmentDialog') private attachmentDialog: Dialog;
  @ViewChild('attachmentDialogTabView') private attachmentDialogTabView: TabView;

  @Input() strategyId: number; // TODO remove strategy id dependency
  private location: string; // This maintains the `<page>-<category>-<subcategory>` references

  @Output() resourcesChange = new EventEmitter<void>();
  // @Output() resourceCreate = new EventEmitter<IResource>();
  @Output() resourceEdit = new EventEmitter<IResource>();
  @Output() resourceCreated = new EventEmitter<IResource>();

  private _externalLinkForm: FormGroup;
  private _attachFilesForm: FormGroup;

  private pristineResource: IResource;

  highlightFileSelector = false;

  /** ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ **/
  /**                     INITIALIZATION                       **/
  /** ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ **/

  constructor(private formBuilder: FormBuilder,
              private validationService: ValidationService,
              private resourceService: ResourceService,
              private _loadingService: AppLoadingService,
              private tokenService: TokenService) { }

  ngOnInit() {}

  show(cLocation: string) {
    this.location = cLocation;
    this.createFormsIfNotExist();
    this.attachmentDialogTabView.activeIndex = this.ATTACH_FILES_TAB_INDEX;
    this.attachmentDialogTabView.tabs[this.ATTACH_FILES_TAB_INDEX].disabled = false;
    this.attachmentDialogTabView.tabs[this.EXTERNAL_LINK_TAB_INDEX].disabled = false;
    this.attachmentDialog.visible = true;
    this.attachmentDialog.modal = true;
    this.pristineResource = undefined;
    this.externalLinkFormReset();
    this.attachFilesFormReset();
    this.logStartup('CREATE');
  }

  edit(cLocation: string, resource: IResource) {
    this.location = cLocation;
    this.createFormsIfNotExist();
    this.resourceToForm(resource);
    this.attachmentDialog.visible = true;
    this.attachmentDialog.modal = true;
    this.attachmentDialogTabView.activeIndex = (resource.isUrl) ? this.EXTERNAL_LINK_TAB_INDEX : this.ATTACH_FILES_TAB_INDEX;
    this.attachmentDialogTabView.tabs[(resource.isUrl) ? this.ATTACH_FILES_TAB_INDEX : this.EXTERNAL_LINK_TAB_INDEX].disabled = true;
    this.logStartup('EDIT');
  }

  createFormsIfNotExist() {
    if (!this.attachFilesForm) {
      this.createAttachFilesForm();
    }
    if (!this.externalLinkForm) {
      this.createExternalLinkForm();
    }
  }


  logStartup(mode: string) {
    console.log(`Attachment Upload Modal Initialized \
    - ${mode} Mode \
    - Location: ${this.location} \
    - Strategy Id: ${this.strategyId} \
    - Tab Index: ${this.attachmentDialogTabView.activeIndex}`);
  }

  /** ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ **/
  /**                         GETTERS                          **/
  /** ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ **/

  /**
   * TODO: Add Context
   * @returns {boolean}
   */
  get hasFile(): boolean {
    return !!this.fileUpload && this.fileUpload.hasFiles();
  }

  /**
   * TODO: Add Context
   * @returns {string}
   */
  get resourceUploadEvidenceUrl() {
    return this.resourceService.getResourceUploadEvidenceUrl(this.strategyId);
  }

  /**
   * Returns the currently selected tab index
   * @returns {number}
   */
  private get selectedTabIndex() {
    return this.attachmentDialogTabView.findTabIndex(this.attachmentDialogTabView.findSelectedTab());
  }

  /**
   * Returns existing attach files form or creates a new one
   * @returns {FormGroup}
   */
  get attachFilesForm(): FormGroup {
    if (!this._attachFilesForm) {
      this._attachFilesForm = this.createAttachFilesForm();
    }
    return this._attachFilesForm;
  }

  /**
   * Returns existing link form or creats a new one
   * @returns {FormGroup}
   */
  get externalLinkForm(): FormGroup {
    if (!this._externalLinkForm) {
      this._externalLinkForm = this.createExternalLinkForm();
    }
    return this._externalLinkForm;
  }

  /**
   * Gets the remaining characters allowed for a file name
   * @returns {number}
   */
  get attachmentFileNameRemainingCharacters() {
    return this.MAX_NAME_LENGTH - this.attachmentFileName.value.length;
  }

  /**
   * Gets the remaining characters allowed for a file description
   * @returns {number}
   */
  get attachmentFileDescriptionRemainingCharacters() {
    return this.MAX_DESCRIPTION_LENGTH - this.attachmentFileDescription.value.length;
  }

  /**
   * Gets the attached fileon
   * @returns {AbstractControl | null}
   */
  get attachmentFile() {
    return this.attachFilesForm.get('attachmentFile');
  }

  /**
   * Gets the attached file name
   * @returns {AbstractControl | null}
   */
  get attachmentFileName() {
    return this.attachFilesForm.get('attachmentFileName');
  }

  /**
   * Gets the attached file description
   * @returns {AbstractControl | null}
   */
  get attachmentFileDescription() {
    return this.attachFilesForm.get('attachmentFileDescription');
  }

  /**
   * Gets the file selection status
   * @returns {IResource}
   */
  get attachmentSelection() {
    return this.pristineResource;
  }

  /**
   * TODO: Add Context
   * @returns {number}
   */
  get externalLinkRemainingCharacters() {
    return this.MAX_LINK_LENGTH - this.externalLink.value.length;
  }

  /**
   * TODO: Add Context
   * @returns {number}
   */
  get externalLinkNameRemainingCharacters() {
    return this.MAX_NAME_LENGTH - this.externalLinkName.value.length;
  }

  /**
   * TODO: Add Context
   * @returns {number}
   */
  get externalLinkDescriptionRemainingCharacters() {
    return this.MAX_DESCRIPTION_LENGTH - this.externalLinkDescription.value.length;
  }

  /**
   * TODO: Add Context
   * @returns {AbstractControl | null}
   */
  get externalLink() {
    return this.externalLinkForm.get('externalLink');
  }

  /**
   * TODO: Add Context
   * @returns {AbstractControl | null}
   */
  get externalLinkName() {
    return this.externalLinkForm.get('externalLinkName');
  }

  /**
   * TODO: Add Context
   * @returns {AbstractControl | null}
   */
  get externalLinkDescription() {
    return this.externalLinkForm.get('externalLinkDescription');
  }

  /**
   * TODO: Add Context
   * @returns {boolean}
   */
  get isSaveDisabled() {
    if (this.selectedTabIndex === this.EXTERNAL_LINK_TAB_INDEX) {
      return !this.externalLinkForm.valid || this.externalLinkForm.pristine || this.isPristine(this.formToResource());
    } else {
      return !this.attachFilesForm.valid || this.attachFilesForm.pristine || this.isPristine(this.formToResource());
    }
  }

  /**
   * TODO: Add Context
   * @returns {boolean}
   */
  get isDialogEditAttachement() {
    return !!this.pristineResource;
  }
  /** ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ **/
  /**                          EVENTS                          **/
  /** ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ **/

  /**
   * Handles reseting forms when tabs change
   * @param event
   */
  onTabChange(event) {
    this.attachFilesFormReset();
    this.externalLinkFormReset();
  }

  /**
   * Executes on file selection
   * @param {{files: FileList; originalEvent: Event}} event
   */
  onSelect(event: {files: FileList, originalEvent: Event}) {
    const files: File[] = [];
    for (let i = 0; i < event.files.length; i++) {
      files.push(event.files[i]);
    }
    this.highlightFileSelector = false;
    this.attachmentFile.setValue(files);
    this.attachmentFile.markAsTouched();
    if (event.originalEvent.type === 'drop') {
      // TODO address validation message not showing the first drop
    }
  }

  /**
   * Updates the formdata payload with file metadata
   * @param {{xhr: XMLHttpRequest; formData: FormData}} event
   */
  onBeforeUpload(event: {xhr: XMLHttpRequest, formData: FormData}) {
    const resource = this.formToResource();
    event.formData.append('fileName', resource.name);
    event.formData.append('fileDescription', resource.description);
    event.formData.append('evidenceType', resource.evidenceType);
  }

  /**
   * Updates the API call with token information
   * NOTE: This could be remove and the logic moved to a permanent interceptor
   * @param {{xhr: XMLHttpRequest; formData: FormData}} event
   */
  onBeforeSend(event: {xhr: XMLHttpRequest, formData: FormData}) {
    event.xhr.setRequestHeader('Authorization', this.tokenService.token);
  }

  /**
   * Executes when a file is removed from the attachment modal
   * @param {{originalEvent: Event; file: File}} event
   */
  onRemove(event: {originalEvent: Event, file: File}) {
    const files: File[] = this.attachmentFile.value;
    this.attachmentFile.setValue(files.filter(it => it !== event.file));
  }

  /**
   * Clears the file
   */
  onClear() {
    this.attachmentFile.setValue([]);
  }

  /**
   * Closes the modal and reports back to parent component
   * @param event
   */
  onUpload(event) {
    this.close();
    this.resourceCreated.emit();
    this._loadingService.clearLoading();
  }

  /**
   * Executes on error
   * @param {{xhr: XMLHttpRequest; files: File[]}} event
   */
  onError(event: {xhr: XMLHttpRequest, files: File[]}) {
    this.close();
  }


  /**
   * TODO: Add Context
   */
  onSaveAttachment() {
    this._loadingService.setLoading();
    const resource = this.formToResource();
    if (this.pristineResource) {
      this.resourceEdit.emit(resource);
    } else if (resource.isUrl) {
      this.createResource(resource);
    } else {
      // create file resource
      this.fileUpload.upload();
    }
    this.close();
  }

  /** ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ **/
  /**                      FORM FUNCTIONS                      **/
  /** ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ **/

  /**
   * Creates a new external link form
   * @returns {any}
   */
  private createExternalLinkForm() {
    return this.formBuilder.group({
      externalLink: ['', [control => this.validateExternalLink(control), this.validationService.resourceLinkValidator]],
      externalLinkName: ['', control => this.validateAttachmentName(control)],
      externalLinkDescription: ['']
    });
  }

  /**
   * Creates a new attach files form
   * @returns {any}
   */
  private createAttachFilesForm() {
    return this.formBuilder.group({
      attachmentFile: [[], (it) => this.attachmentFileValidate(it)],
      attachmentFileName: ['', control => this.validateAttachmentName(control)],
      attachmentFileDescription:  ['']
    });
  }

  /**
   * Resets the attach files form to default state
   * @param {{attachmentFile: File[]; attachmentFileName: string; attachmentFileDescription: string}} fomData
   */
  private attachFilesFormReset(fomData = {attachmentFile: [] as File[], attachmentFileName: '', attachmentFileDescription: ''}) {
    if (this.fileUpload) {
      this.fileUpload.clear();
    }
    this.attachFilesForm.reset(fomData);
  }

  /**
   * Resets the external link form to default state
   * @param {{externalLink: string; externalLinkName: string; externalLinkDescription: string}} fomData
   */
  private externalLinkFormReset(fomData = {externalLink: '', externalLinkName: '', externalLinkDescription: ''}) {
    this.externalLinkForm.reset(fomData);
  }

  /**
   * TODO: Add Context
   * @param {IResource} resource
   * @returns {boolean}
   */
  private isPristine(resource: IResource) {
    return this.pristineResource &&
      resource.name === this.pristineResource.name &&
      resource.description === this.pristineResource.description &&
      (!resource.isUrl || resource.resourcePath === this.pristineResource.resourcePath);
  }


  /** ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ **/
  /**                   VALIDATION FUNCTIONS                   **/
  /** ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ **/

  /**
   * Validates the existence of an external link
   * @param {AbstractControl} control
   * @returns {any}
   */
  private validateExternalLink(control: AbstractControl) {
    if (!control.value.trim().length) {
      return {externalLinkUrlRequired: true, error: true};
    } else {
      return null;
    }
  }

  /**
   * Validates the existence of an attachment name
   * @param {AbstractControl} control
   * @returns {any}
   */
  private validateAttachmentName(control: AbstractControl) {
    if (!control.value.trim().length) {
      return {attachmentFileNameRequired: true, error: true};
    } else {
      return null;
    }
  }

  /**
   * Validates file existence, file size, file type
   * @param {AbstractControl} control
   * @returns {ValidationErrors | null}
   */
  private attachmentFileValidate(control: AbstractControl): ValidationErrors | null {
    let errors = null;
    const files: File[] = control.value;
    if (this.pristineResource) {
      if (files.length) {
        errors = {attachmentFileNotEditable: true, error: true};
      }
    } else if (!files.length) {
      errors = {attachmentFileRequired: true, error: true};
    } else if (files[0].size > ResourceService.MAX_FILE_SIZE) {
      errors = {attachmentFileSize: true, error: true};
    } else if (!this.isValidExtension(files[0])) {
      errors = {attachmentFileType: true, error: true};
    }
    return errors;
  }

  /**
   * Validates file extension
   * @param {File} file
   * @returns {boolean}
   */
  isValidExtension(file: File) {
    const ext = file.name.match(this.EXTENSION_RE)[0].toLowerCase();
    return ResourceService.ACCEPT_EXTENSIONS.includes(ext);
  }

  /** ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ **/
  /**                   UNORGANIZED FUNCTIONS                  **/
  /** ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ **/
  // TODO: Organize These Into Proper Areas

  /**
   * TODO: Add Context
   * @returns {IResource}
   */
  private formToResource(): IResource {
    let resource: IResource;
    if (this.selectedTabIndex === this.EXTERNAL_LINK_TAB_INDEX) {
      const formModel = this.externalLinkForm.value;
      resource = {
        isUrl: true,
        resourcePath: formModel.externalLink,
        name: formModel.externalLinkName,
        description: formModel.externalLinkDescription,
        evidenceType: this.location
      };
    } else {
      const formModel = this.attachFilesForm.value;
      resource = {
        isUrl: false,
        name: formModel.attachmentFileName,
        description: formModel.attachmentFileDescription,
        evidenceType: this.location
      };
    }
    if (this.pristineResource) {
      resource.resourceId = this.pristineResource.resourceId;
    }
    return resource;
  }

  /**
   * TODO: Add Context
   * @param {File} file
   * @returns {string}
   */
  iconImageSrcForFile(file: File) {
    let iconType = 'other';
    const fileExt = file.name.match(this.EXTENSION_RE)[0].toLowerCase();
    if (fileExt === '.doc' || fileExt === '.docx' || fileExt === '.txt' || fileExt === '.rtf') {
      iconType = 'doc';
    } else if (fileExt === '.xls' || fileExt === '.xlsx' || fileExt === '.csv') {
      iconType = 'spreadsheet';
    } else if (fileExt === '.ppt' || fileExt === '.pptx') {
      iconType = 'presentation';
    } else if (fileExt === '.pdf') {
      iconType = 'pdf';
    } else if (fileExt === '.jpg' || fileExt === '.jpeg' || fileExt === '.gif' || fileExt === '.bmp' || fileExt === '.png' ||
      fileExt === '.svg') {
      iconType = 'image';
    }
    return `assets/icons/icon-filetype-${iconType}.svg`;
  }

  /**
   * TODO: Add Context
   * @param {IResource} resource
   * @returns {string}
   */
  iconImageSrc(resource: IResource) {
    return `assets/icons/${resource.fileGroupIcon}`;
  }

  /**
   * TODO: Add Context
   * @param {IResource} resource
   * @returns {string}
   */
  altText(resource: IResource) {
    return resource.isUrl ? 'External Link' : 'Attached File';
  }

  /**
   * TODO: Add Context
   * @param {IResource} resource
   */
  private resourceToForm(resource: IResource) {
    this.pristineResource = resource;
    if (resource.isUrl) {
      this.externalLinkFormReset(
        {externalLink: resource.resourcePath, externalLinkName: resource.name, externalLinkDescription: resource.description});
      this.attachFilesFormReset();
    } else {
      this.attachFilesFormReset({attachmentFile: [], attachmentFileName: resource.name, attachmentFileDescription: resource.description});
      this.externalLinkFormReset();
    }
  }

  /**
   * Manual close modal method
   */
  close() {
    this.attachmentDialog.visible = false;
    this.attachmentDialog.modal = false;
    this.highlightFileSelector = false;
    this.attachmentDialogTabView.activeIndex = this.ATTACH_FILES_TAB_INDEX;
  }


  createResource(resource: IResource) {
    if (resource.isUrl) {
      // create external link resource
      this.resourceService.createLinkResource(this.strategyId, resource)
        .subscribe(() =>  {
          this._loadingService.clearLoading();
          this.resourceCreated.emit();
        }, (err) => {
          this._loadingService.clearLoading();
          console.error(`Caught error in creating resource: ${JSON.stringify(err)}`);
        });
    } else {
      console.warn('onResourceCreate', resource);
    }
  }

  clearSelectedFile(event, index) {
    this.attachmentFile.markAsUntouched();
    this.highlightFileSelector = true;
    this.fileUpload.remove(event, index);
  }

}
